
import {Apollo, QueryRef} from 'apollo-angular';
import { ApolloClient, ObservableQuery, ApolloLink, Cache } from '@apollo/client';
import { of as observableOf,  Observable, BehaviorSubject, combineLatest, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';

import { NotificationService, errorMessage } from '@nuso/common/util-errors';
import { graphQLErrorCode, isNetworkLost, networkErrorOf } from './graph-errors';

import { map, catchError, filter, switchMap, tap, takeWhile, finalize } from 'rxjs/operators';


import { defaults } from 'lodash';

import { NETWORK_STATUS_QUERY, NetworkStatus } from './network-status/network-status.model';
export interface ExtendedWatchQueryOptions<T = any> extends ApolloClient.WatchQueryOptions<T, any> {
  defaultResponse?: any;
  rethrowErrors?: boolean;
  msgPrefix?: string;
}

/** GraphApiQueryRef.watchPoll's pollInterval when the caller sets none. */
export const DEFAULT_POLL_INTERVAL_MS = 10000;

export interface ExtendedMutationOptions extends ApolloClient.MutateOptions {
  msgPrefix?: string;
  defaultResponse?: any;
}

export interface GraphErrorObject {
  code: string;
  message: string | null | undefined;
}

// Typically we just do one query at a time. If that is the case
// return data.whatever
// If we are doing multiple queries, return data and leave it up
// to the user of the watchQuery to drill in.
export function getResultFromData(data: any, options?: ApolloClient.WatchQueryOptions<any, any>) {
  const keys = Object.keys(data);
  // const queryDefn: OperationDefinitionNode = <OperationDefinitionNode>options.query.definitions[0];
  // return data[queryDefn.name.value];

  return keys.length === 1 ? data[keys[0]] : data;
}

export function getMutationResult(result: ApolloLink.Result<any> | null | undefined) {
  return (result && result.data) ? getResultFromData(result.data) : result;
}

export function graphErrorMessage(err: { message?: string } | null | undefined): string | null | undefined {
  if (!err) {
    return null;
  }
  if (err.message) {
    return errorMessage(err);
  }
  return err.message;
}

export function graphErrorObject(err: any): GraphErrorObject {
  return {
    code: graphQLErrorCode(err) ?? 'UNKNOWN_ERROR',
    message: graphErrorMessage(err)
  }
}

interface GraphRefetchPromiseCallbacks<T> {
  resolve: (value: ObservableQuery.Result<T>) => void;
  reject: (reason?: any) => void;
}

export class GraphApiQueryRef<T> {
  // Need this in case an error happens stopping the inner
  // getResult Observable, and then refetch is called.
  // We'd like to startup a new watchQuery in this case.
  restartRefetch$ = new BehaviorSubject<GraphRefetchPromiseCallbacks<T> | null>(null);

  constructor(public queryRef: QueryRef<T> | null, public options: ExtendedWatchQueryOptions<T>, private api: GraphApiService) {
  }

  refetch(variables?: any) {
    if (this.queryRef) {
      return this.queryRef.refetch(variables);
    } else {
      return new Promise((resolve, reject) => {
        this.restartRefetch$.next({ resolve, reject });
      });
    }
  }

  getResult(refetchPromiseCallBacks?: GraphRefetchPromiseCallbacks<T> | null) {
    if (!this.queryRef) {
      throw new Error('GraphApiQueryRef.getResult called with no active queryRef');
    }
    return this.queryRef.valueChanges
      .pipe(
        // Apollo Client 4 reports errors on `next` (the stream itself never errors), so
        // rethrow them into the stream: the catchError below then handles them as before,
        // and the stream ends so that refetch restarts the query. With `errorPolicy: 'all'`
        // a result with both data and an error is delivered as data (partial data, no
        // toast); without usable data it is still a failure.
        tap(result => {
          if (result.error && !(this.options.errorPolicy === 'all' && result.data)) {
            throw result.error;
          }
        }),
        filter(result => !!result.data),
        tap(result => {
          if (refetchPromiseCallBacks) {
            refetchPromiseCallBacks.resolve(result);
          }
        }),
        map(result => getResultFromData(result.data, this.options)),
        catchError((err) => {
          if (refetchPromiseCallBacks) {
            refetchPromiseCallBacks.reject(err);
          }
          return this.api.handleError(err, this.options);
        }),
        finalize(() => this.queryRef = null)
      );
  }

  fetchMore(options: Parameters<QueryRef<T>['fetchMore']>[0] & ObservableQuery.FetchMoreOptions<any, any>) {
    if (!this.queryRef) {
      throw new Error('GraphApiQueryRef.fetchMore called with no active queryRef');
    }
    return this.queryRef.fetchMore(options);
  }

  watchPoll() {
    const defaultOptions = {
      pollInterval: DEFAULT_POLL_INTERVAL_MS,
      fetchPolicy: 'cache-and-network',
    };

    const connectedToNetwork$ = this.api.apollo.watchQuery({ query: NETWORK_STATUS_QUERY } )
      .valueChanges.pipe(
        filter(result => !!result.data),
        map(result => getResultFromData(result.data)),
        takeWhile((networkStatus: NetworkStatus) => !networkStatus.isAuthenticationError),
        filter((networkStatus: NetworkStatus) => networkStatus.isConnected) // && !networkStatus.isAuthenticationError)
      );

    return combineLatest([connectedToNetwork$, this.restartRefetch$])
      .pipe(
        switchMap(([networkState, refetchPromiseCallbacks]) => {
          this.queryRef = this.api.apollo.watchQuery<T>(defaults(this.options, defaultOptions));
          return this.getResult(refetchPromiseCallbacks);
        })
      )
  }
}

@Injectable({
  providedIn: 'root'
})
export class GraphApiService {
  apollo = inject(Apollo);
  private notificationService = inject(NotificationService);


  handleError(error: unknown, options: ExtendedWatchQueryOptions) {
    // Ignore lost network errors, these are handled globaly
    // But return what is in the cache
    const defaultResponse = options.defaultResponse !== undefined ? options.defaultResponse : null;
    if (isNetworkLost(networkErrorOf(error))) {
      const cachedResponse = this.apollo.client.readQuery(options);
      return cachedResponse
        ? observableOf(getResultFromData(cachedResponse, options))
        : observableOf(defaultResponse);
    } else {
      if (options.rethrowErrors) {
        return throwError(() => error);
      }
      else {
        this.notificationService.addGraphqlError(error, options.msgPrefix);
        return observableOf(defaultResponse);
      }
    }
  }

  watchQueryRef<T>(options: ExtendedWatchQueryOptions<T>): GraphApiQueryRef<T> {
    const queryRef = this.apollo.watchQuery<T>(options);

    return new GraphApiQueryRef(queryRef, options, this);
  }

  watchQuery<T>(options: ExtendedWatchQueryOptions<T>): Observable<T> {
    const query = this.watchQueryRef<T>(options);
    return query.getResult();
  }

  pollQueryRef<T>(options: ExtendedWatchQueryOptions<T>): GraphApiQueryRef<T> {
    return new GraphApiQueryRef(null, options, this);
  }

  pollQuery<T>(options: ExtendedWatchQueryOptions<T>): Observable<T> {
    const query = this.pollQueryRef(options);
    return query.watchPoll();
  }

  // Default to `any` (Apollo Client 3's default): callers read `.data.x` untyped.
  query<T = any>(options: ApolloClient.QueryOptions<T>) {
    return this.apollo.query<T>(options);
  }

  // Default to `any` (Apollo Client 3's default): callers read `.data.x` untyped.
  mutate<T = any>(options: ApolloClient.MutateOptions<T>) {
    return this.apollo.mutate<T>({
      ...options,
      context: {
        headers: {
          'portal-path': window.location.pathname
        }
      }
    });
  }

  mutateWithCatch(options: ExtendedMutationOptions) {
    return this.mutate(options).pipe(
      map(result => getMutationResult(result)),
      this.notificationService.catchGraphError(options.msgPrefix as string, options.defaultResponse),
    );
  }

  readQuery<T>(options: ExtendedWatchQueryOptions<T>): T {
    return this.apollo.client.readQuery(options) as T;
  }

  writeQuery(options: Cache.WriteQueryOptions<any, any>) {
    this.apollo.client.writeQuery(options);
  }
}
